/*
 * Copyright 2021 Michael Kraley
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import styles from "./Recipe.module.css";
import {
  Text,
  TextField,
  Tooltip,
  TooltipTrigger,
  Button,
} from "@adobe/react-spectrum";
import { useRecipeAdminContext } from "./RecipeBox";
import { useState } from "react";
import CheckmarkCircle from "@spectrum-icons/workflow/CheckmarkCircle";
import CloseCircle from "@spectrum-icons/workflow/CloseCircle";
import Circle from "@spectrum-icons/workflow/Circle";
import { addLogRecord } from "./logging";

const ThumbUpButton = (props: { recipeName: string | null }) => {
  const [clickedGood, setClickedGood] = useState(false);
  const [clickedBad, setClickedBad] = useState(false);
  const [clickedUnsure, setClickedUnsure] = useState(false);

  // TODO can simplify code with indicator variable for Up/Down function
  return (
    <div>
      <span
        onClick={() => {
          if (props.recipeName !== null) {
            addLogRecord(
              "recipeRateGood",
              props.recipeName,
              clickedGood,
              !clickedGood
            );
            setClickedGood(!clickedGood);
          }
          if (clickedBad) {
            setClickedBad(!clickedBad);
          }
          if (clickedUnsure) {
            setClickedUnsure(!clickedUnsure);
          }
        }}
      >
        {clickedGood ? (
          <CheckmarkCircle color="positive" size="S" marginTop="0.4em" />
        ) : (
          <CheckmarkCircle size="S" marginTop="0.4em" />
        )}
      </span>
      <span
        onClick={() => {
          if (props.recipeName !== null) {
            addLogRecord(
              "recipeRateUnsure",
              props.recipeName,
              clickedUnsure,
              !clickedUnsure
            );
            setClickedUnsure(!clickedUnsure);
          }
          if (clickedGood) {
            setClickedGood(!clickedGood);
          }
          if (clickedBad) {
            setClickedBad(!clickedBad);
          }
        }}
      >
        {clickedUnsure ? (
          <Circle color="notice" size="S" marginTop="0.4em" />
        ) : (
          <Circle size="S" marginTop="0.4em" />
        )}
      </span>
      <span
        onClick={() => {
          if (props.recipeName !== null) {
            addLogRecord(
              "recipeRateBad",
              props.recipeName,
              clickedBad,
              !clickedBad
            );
            setClickedBad(!clickedBad);
          }
          if (clickedGood) {
            setClickedGood(!clickedGood);
          }
          if (clickedUnsure) {
            setClickedUnsure(!clickedUnsure);
          }
        }}
      >
        {clickedBad ? (
          <CloseCircle color="negative" size="S" marginTop="0.4em" />
        ) : (
          <CloseCircle size="S" marginTop="0.4em" />
        )}
      </span>
    </div>
  );
};

const Recipe = (props: {
  id: number;
  name: string | null;
  onClick: () => void;
  setName: (name: string) => void;
  remove: () => void;
}) => {
  const recipeAdmin = useRecipeAdminContext();

  return (
    <>
      {props.name ? (
        <TooltipTrigger>
          <button
            onClick={props.onClick}
            className={styles.Recipe}
            id={"recipe" + String(props.id)}
            style={{ width: "9em", textAlign: "center" }}
          >
            <Text>{props.name}</Text>
            {recipeAdmin.showDelete && (
              <div
                className={styles.Remove}
                onClick={(e) => {
                  e.stopPropagation();
                  props.remove();
                }}
              >
                x
              </div>
            )}
          </button>
          <Tooltip>Restore control settings from this Recipe</Tooltip>
        </TooltipTrigger>
      ) : (
        <div>
          <TextField
            label="Name"
            labelPosition="side"
            autoFocus={true}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                props.setName((e.target as HTMLInputElement).value);
              }
              if (e.key === "Escape") {
                props.remove();
              }
            }}
          />
        </div>
      )}
      {/* <ThumbUpButton recipeName={props.name}/> */}
    </>
  );
};

export default Recipe;
